/**
 * Internal Testing Page - PoC validation tool
 * TRNS-143: Password-protected testing interface for the translation pipeline
 */

import { useState, useEffect } from 'react';
import { TestingForm } from '../components/TestingForm';
import {
  PipelineConfig,
  PromptWeighting,
  TestingContext,
  TestingPreset,
  TestingResult,
  TestingApiResponse,
} from '../types';
import {
  runTranslation,
  checkHealth,
  isPasswordVerified,
  setPasswordVerified,
  loadPresets,
  savePreset,
  deletePreset,
  loadResults,
  saveResult,
  clearResults,
  exportResultsToJSON,
} from '../services/testingService';
import './InternalTestingPage.css';

const TESTING_PASSWORD = 'translayshun2026';

export function InternalTestingPage() {
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const [latestResult, setLatestResult] = useState<TestingApiResponse | null>(null);
  const [error, setError] = useState('');

  const [presets, setPresets] = useState<TestingPreset[]>([]);
  const [presetName, setPresetName] = useState('');
  const [results, setResults] = useState<TestingResult[]>([]);

  const [apiHealthy, setApiHealthy] = useState<boolean | null>(null);

  // Check auth on mount
  useEffect(() => {
    if (isPasswordVerified()) {
      setAuthenticated(true);
    }
  }, []);

  // Load presets and results on auth
  useEffect(() => {
    if (authenticated) {
      setPresets(loadPresets());
      setResults(loadResults());
      // Check API health
      checkHealth().then((h) => setApiHealthy(h !== null));
    }
  }, [authenticated]);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === TESTING_PASSWORD) {
      setAuthenticated(true);
      setPasswordVerified(true);
      setLoginError('');
    } else {
      setLoginError('Incorrect password');
    }
  };

  const handleLogout = () => {
    setAuthenticated(false);
    setPasswordVerified(false);
  };

  const handleTranslate = async (params: {
    text: string;
    sourceLang: string;
    targetLang: string;
    context: TestingContext;
    pipeline: PipelineConfig;
    weighting: PromptWeighting;
  }) => {
    setIsLoading(true);
    setError('');
    setLatestResult(null);

    const response = await runTranslation({
      text: params.text,
      sourceLang: params.sourceLang,
      targetLang: params.targetLang,
      mode: params.pipeline.mode,
      context: params.context,
      pipeline: params.pipeline,
      weighting: params.weighting,
    });

    setLatestResult(response);
    setIsLoading(false);

    if (!response.success) {
      setError(response.error?.message || 'Translation failed');
      return;
    }

    // Save result
    const result: TestingResult = {
      id: crypto.randomUUID(),
      sourceText: params.text,
      sourceLang: params.sourceLang,
      targetLang: params.targetLang,
      context: params.context,
      pipeline: params.pipeline,
      weighting: params.weighting,
      response,
      timestamp: new Date().toISOString(),
    };
    saveResult(result);
    setResults(loadResults());
  };

  const handleSavePreset = () => {
    if (!presetName.trim()) return;
    // We'll save a preset with current defaults (the form persists its own state)
    const preset: TestingPreset = {
      id: crypto.randomUUID(),
      name: presetName.trim(),
      sourceLang: 'en',
      targetLang: 'es',
      context: { gender: 'neutral', register: 'informal', tone: 'casual' },
      pipeline: { mode: 'write', temperature: 0.7, topP: 0.9, maxTokens: 2048 },
      weighting: { accuracy: 50, naturalness: 50 },
      createdAt: new Date().toISOString(),
    };
    savePreset(preset);
    setPresets(loadPresets());
    setPresetName('');
  };

  const handleDeletePreset = (id: string) => {
    deletePreset(id);
    setPresets(loadPresets());
  };

  const handleClearResults = () => {
    clearResults();
    setResults([]);
  };

  const handleExportResults = () => {
    exportResultsToJSON(results);
  };

  // Login screen
  if (!authenticated) {
    return (
      <div className="testing-login">
        <div className="testing-login-card">
          <h1>Internal Testing</h1>
          <p>Translation Pipeline Validation Tool</p>
          <form onSubmit={handleLogin}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              autoComplete="current-password"
              name="testing-password"
            />
            <button type="submit">Access Testing</button>
            {loginError && <p className="login-error">{loginError}</p>}
          </form>
        </div>
      </div>
    );
  }

  // Main testing page
  return (
    <div className="testing-page">
      <header className="testing-header">
        <div className="header-info">
          <h1>Internal Testing</h1>
          <p>Translation Pipeline Validation</p>
        </div>
        <div className="header-actions">
          {apiHealthy !== null && (
            <span className={`health-badge ${apiHealthy ? 'healthy' : 'unhealthy'}`}>
              API: {apiHealthy ? 'Connected' : 'Offline'}
            </span>
          )}
          <button onClick={handleLogout} className="logout-btn">
            Logout
          </button>
        </div>
      </header>

      <div className="testing-layout">
        {/* Preset Manager */}
        <div className="testing-card preset-card">
          <h3 className="card-title">Presets</h3>
          <div className="preset-save">
            <input
              type="text"
              value={presetName}
              onChange={(e) => setPresetName(e.target.value)}
              placeholder="Preset name..."
              className="preset-name-input"
            />
            <button
              onClick={handleSavePreset}
              disabled={!presetName.trim()}
              className="preset-save-btn"
            >
              Save
            </button>
          </div>
          {presets.length > 0 && (
            <div className="preset-list">
              {presets.map((p) => (
                <div key={p.id} className="preset-item">
                  <span className="preset-item-name">{p.name}</span>
                  <button
                    onClick={() => handleDeletePreset(p.id)}
                    className="preset-delete-btn"
                    title="Delete preset"
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
          )}
          {results.length > 0 && (
            <div className="results-actions">
              <button onClick={handleExportResults} className="export-btn">
                Export Results ({results.length})
              </button>
              <button onClick={handleClearResults} className="clear-btn">
                Clear
              </button>
            </div>
          )}
        </div>

        {/* Form */}
        <TestingForm onSubmit={handleTranslate} isLoading={isLoading} />

        {/* Error Display */}
        {error && (
          <div className="testing-card error-card">
            <p className="error-text">{error}</p>
          </div>
        )}

        {/* Output Display */}
        {latestResult?.success && latestResult.data && (
          <div className="testing-card output-card">
            <h3 className="card-title">Translation Result</h3>

            {/* Translated text */}
            <div className="translated-text">
              {latestResult.data.translatedText}
            </div>

            {/* Quality metrics row */}
            <div className="metrics-row">
              <div className="metric">
                <span className="metric-label">Confidence</span>
                <span className="metric-value">
                  {(latestResult.data.metadata.confidence * 100).toFixed(1)}%
                </span>
              </div>
              <div className="metric">
                <span className="metric-label">Processing Time</span>
                <span className="metric-value">
                  {latestResult.data.metadata.processingTime}ms
                </span>
              </div>
              <div className="metric">
                <span className="metric-label">Passes</span>
                <span className="metric-value">
                  {Object.keys(latestResult.data.metadata.passes).length}
                </span>
              </div>
              <div className="metric">
                <span className="metric-label">Mode</span>
                <span className="metric-value">{latestResult.data.mode}</span>
              </div>
            </div>

            {/* Variants */}
            {latestResult.data.variants && latestResult.data.variants.length > 1 && (
              <div className="variants-section">
                <h4>Variants</h4>
                {latestResult.data.variants.map((v, i) => (
                  <div key={i} className="variant-item">
                    <span className="variant-text">{v.text}</span>
                    <span className="variant-confidence">
                      {(v.confidence * 100).toFixed(1)}%
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Pipeline Trace */}
            <details className="pipeline-trace">
              <summary>Pipeline Trace (JSON)</summary>
              <pre className="trace-json">
                {JSON.stringify(latestResult.data.metadata.passes, null, 2)}
              </pre>
            </details>

            {/* Full Response */}
            <details className="pipeline-trace">
              <summary>Full Response (JSON)</summary>
              <pre className="trace-json">
                {JSON.stringify(latestResult.data, null, 2)}
              </pre>
            </details>
          </div>
        )}
      </div>

      <footer className="testing-footer">
        <p>Translayshun Internal Testing - {new Date().toLocaleDateString()}</p>
      </footer>
    </div>
  );
}
